import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";
import { RouteComponentProps } from "react-router-dom";
import URI from "urijs";
import {
  LowIncomeSubsidyStatus,
  ActionType,
  PlanType,
  PlanTypeLabels,
  SearchResultPlan,
} from "../../@types";
import { AppPage, FocusableHeader, PlanCard, Pager } from "../../components";
import {
  fetchCurrentPlan,
  doFiltersExist,
  isMedicareAdvantageType,
  useTranslate,
} from "../../helpers";
import PlanFiltersContainer from "./PlanFiltersContainer";
import PlanCompareFooter from "./PlanCompareFooter";
import { FormattedMessage } from "react-intl-phraseapp";
import SortSelector from "./SortSelector";
import SpecialNeedsPlansFilters from "./SpecialNeedsPlansFilters";
import { isCurrentPlan } from "../../helpers/beneficiaryInfoHelpers";
import { AppStore, defaultState } from "../../app/store";
import CoveragePlanCard from "./CoveragePlanCard";
import OriginalMedicarePlanCard from "./OriginalMedicarePlanCard";
import SearchResultsPageHeader from "./SearchResultsPageHeader";
import {
  useSearchResultsHelper,
  SearchResultsState,
} from "./useSearchResultsHelper";
import OriginalMedicareHelpDrawer from "./OriginalMedicareHelpDrawer";

/**
 * The Search Results page displays [[PlanCard]]s for each plan returned from the API.
 *
 * This page receives a search string from [[FindPlanPage]] via react-router's `location.search` prop. This string contains a 'fips' code which is used to identify which plans are available to the user based on their geographic location.
 */
const SearchResults: FunctionComponent<RouteComponentProps> = props => {
  const { state, dispatch: appDispatch } = useContext(AppStore.AppContext);
  const [pageState, setPageState] = useState({
    currentPage: 0,
    includeOriginalMedicare: false,
    loading: true,
    oldZip: state.zipcode,
    otherPlansAvailable: 0,
    pageCount: 0,
    plans: [],
    plansToCompareErrorTimer: 0,
    plansToCompareExceeded: false,
    showOriginalMedicareDrawer: false,
    showSwitchPlansHelp: false,
    total_results: 0,
    availableCategories: undefined,
    showSeniorSavingsModelFilter: false,
    initialCallMade: false,
  } as SearchResultsState);

  const updatePageQuery = (page: number): void => {
    const query = new URI(props.location.search);
    query.setSearch("page", (page + 1).toString());
    props.history.push(`${props.location.pathname}?${query.query()}`);
  };

  const {
    appState,
    appState: { searchResultsFilters },
    resetToFirstPageOfResults,
    updatePageState,
    defaultSort,
    ...SRHelper
  } = useSearchResultsHelper(
    state,
    appDispatch,
    pageState,
    setPageState,
    updatePageQuery
  );

  const isMA = isMedicareAdvantageType(appState.planType || PlanType.MAPD);

  // fetch the current and next year plan cards in case pharmacies or drugs changed
  useEffect(() => {
    fetchCurrentPlan(
      state.beneficiary,
      state.pharmacies,
      state.lis || LowIncomeSubsidyStatus.LIS_NO_HELP,
      appDispatch,
      state.nextYearCoverage,
      state.futureLis || LowIncomeSubsidyStatus.LIS_NO_HELP
    );
  }, []);

  useEffect(() => {
    if (pageState.includeOriginalMedicare) {
      window.scrollTo(0, 0);
    }
  }, [pageState.includeOriginalMedicare]);

  // If the year changes, we want to clear filters and reset the page to 1
  useEffect(() => {
    if (pageState.initialCallMade) {
      if (doFiltersExist(searchResultsFilters)) {
        appDispatch({
          type: ActionType.UPDATE_SEARCH_RESULTS_FILTERS,
          payload: defaultState.searchResultsFilters,
        });
        resetToFirstPageOfResults();
      }
      SRHelper.updateCompareList(1);
    }
  }, [appState.year]);

  /**
   * Whenever one of the state properties listed below changes, get an updated list of plans
   */
  useEffect(() => {
    const { page: urlPageNumber }: { page: number } = new URI(
      props.location.search
    ).search(true);

    SRHelper.updateCompareList(urlPageNumber);
  }, [
    appState.fips,
    appState.planType,
    appState.snpFilters,
    appState.zipcode,
    searchResultsFilters.filters,
    searchResultsFilters.insuranceCarrier,
    searchResultsFilters.sortBy,
    searchResultsFilters.starRatingFilter,
    searchResultsFilters.drugCoverageFilter,
    searchResultsFilters.planCategoryFilters,
    searchResultsFilters.seniorSavingsModelFilter,
  ]);

  /* Update opposite plans (the count) when fips/planType changes */
  useEffect(() => {
    const { fips, planType, year } = appState;
    if (fips && planType && year) {
      SRHelper.requestOppositePlans();
    }
  }, [appState.fips, appState.zipcode, appState.planType, appState.year]);

  const t = useTranslate();

  const planLabel = appState.planType
    ? t(PlanTypeLabels[appState.planType.toUpperCase()])
    : "";

  const heading = `${pageState.total_results} ${planLabel} ${t(
    "search_results_page.plans_available"
  )}`;

  return (
    <>
      <AppPage
        headerExtension={
          <SearchResultsPageHeader
            pageState={pageState}
            updatePageState={updatePageState}
            {...props}
          />
        }
      >
        <PlanFiltersContainer
          heading={
            <FocusableHeader
              className="e2e-results-plan-type PlanFilters__resultsHeader"
              text={heading}
            />
          }
          resetToFirstPageOfResults={resetToFirstPageOfResults}
          loadingPlans={pageState.loading}
          numPlans={pageState.plans.length}
          availableCategories={pageState.availableCategories}
          showSeniorSavingsModelFilter={pageState.showSeniorSavingsModelFilter}
        />
        {appState.year &&
          appState.year === appState.currentCoverage?.contract_year && (
            <CoveragePlanCard
              useCurrentCoverage={true}
              pageState={pageState}
              {...props}
            />
          )}

        {appState.year &&
          appState.year === appState?.nextYearCoverage?.contract_year && (
            <CoveragePlanCard
              useCurrentCoverage={false}
              pageState={pageState}
              {...props}
            />
          )}

        <div className="ds-l-row e2e-search-results-page ds-u-margin-top--3 ds-u-md-margin-top--2">
          <div className="ds-l-col">
            <div className="SearchResults__totalAndSortContainer">
              <div className="SearchResults__totalResults ds-u-font-weight--semibold">
                <span role="status" aria-live="polite" aria-atomic="true">
                  <FormattedMessage
                    id="search_results_page.total_plans"
                    tagName="span"
                    values={{
                      current: pageState.plans.length,
                      pluralPlanType: isMA
                        ? t("search_results_page.escape_hatch.medicare_plans")
                        : t("search_results_page.escape_hatch.drug_plans"),
                      total: pageState.total_results,
                    }}
                  />
                </span>
              </div>

              <SortSelector
                onSortChanged={resetToFirstPageOfResults}
                defaultSort={defaultSort}
              />
            </div>

            {isMA && (
              <SpecialNeedsPlansFilters
                onSpecialNeedsPlansUpdated={resetToFirstPageOfResults}
              />
            )}

            <div className="ds-u-lg-display--none ds-u-font-weight--semibold">
              <span role="status" aria-live="polite" aria-atomic="true">
                <FormattedMessage
                  id="search_results_page.total_plans"
                  tagName="span"
                  values={{
                    current: pageState.plans.length,
                    pluralPlanType: isMA
                      ? t("search_results_page.escape_hatch.medicare_plans")
                      : t("search_results_page.escape_hatch.drug_plans"),
                    total: pageState.total_results,
                  }}
                />
              </span>
            </div>

            <ol className="e2e-search-results-list ResultsList ds-c-list ds-c-list--bare">
              {pageState.includeOriginalMedicare && (
                <OriginalMedicarePlanCard />
              )}

              {pageState.plans.map((plan: SearchResultPlan) => (
                <li
                  key={plan.id}
                  aria-labelledby={`${plan.id}-heading`}
                  className="ds-u-margin-bottom--4 e2e-plan-card"
                >
                  <PlanCard
                    plan={plan}
                    handleAddToCompare={SRHelper.handleAddToCompareClick}
                    handleRemoveFromCompare={
                      SRHelper.handleRemovePlanFromCompare
                    }
                    isCurrentPlan={isCurrentPlan(
                      appState.currentCoverage,
                      plan
                    )}
                    {...props}
                  />
                </li>
              ))}
            </ol>

            <div className="SearchResults__footer">
              <OriginalMedicareHelpDrawer
                updatePageState={updatePageState}
                pageState={pageState}
                {...props}
              />

              <div>
                <Pager
                  currentPage={pageState.currentPage}
                  pageCount={pageState.pageCount}
                  handlePageClick={(
                    pageNum: number,
                    scrollToTop: boolean
                  ): void => {
                    // istanbul ignore next: tested elsewhere
                    SRHelper.requestPlans(pageNum, scrollToTop);
                  }}
                  onNextPage={SRHelper.onNextPage}
                  onPrevPage={SRHelper.onPrevPage}
                />
              </div>
            </div>

            <div className="ds-l-col ds-u-display--flex ds-u-justify-content--center ds-u-md-justify-content--end mct-hide-print">
              <span className="ds-u-font-size--small ds-u-margin-y--2 ds-u-lg-margin-x--6">
                <FormattedMessage
                  id="search_results_page.total_plans"
                  tagName="span"
                  values={{
                    current: pageState.plans.length,
                    pluralPlanType: isMA
                      ? t("search_results_page.escape_hatch.medicare_plans")
                      : t("search_results_page.escape_hatch.drug_plans"),
                    total: pageState.total_results,
                  }}
                />
              </span>
            </div>
          </div>
        </div>
      </AppPage>
      {appState.compareList.length > 0 &&
        !SRHelper.listYearMismatch &&
        appState.fips && (
          <PlanCompareFooter
            list={appState.compareList}
            onRemovePlanFromComparison={SRHelper.handleRemovePlanFromCompare}
            plansExceededError={pageState.plansToCompareExceeded}
            fips={appState.fips}
            pageLoading={pageState.loading}
          />
        )}
    </>
  );
};

export default SearchResults;
